import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { projectAPI, logsAPI, dashboardAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from '../components/ui/dropdown-menu';
import {
  Plus, FolderOpen, Coins, Activity, Terminal, LogOut, Settings, Shield, ChevronDown,
  Code2, Smartphone, Server, Bot, GitBranch, Clock, Trash2, Download, Sun, Moon,
  AlertTriangle, Bell, AlertOctagon, Zap, Calendar, ListChecks
} from 'lucide-react';
import { toast } from 'sonner';

const typeIcons = { fullstack: Code2, mobile: Smartphone, service: Server, ai_agent: Bot, pipeline: GitBranch };
const typeLabels = { fullstack: 'Full Stack', mobile: 'Mobile', service: 'Service', ai_agent: 'AI Agent', pipeline: 'Pipeline' };

export default function DashboardPage() {
  const { user, logout, theme, setTheme } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [stats, setStats] = useState(null);
  const [risks, setRisks] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [projRes, statsRes, risksRes, remRes] = await Promise.all([
        projectAPI.list(),
        logsAPI.stats(),
        dashboardAPI.risks(),
        dashboardAPI.reminders(),
      ]);
      setProjects(projRes.data);
      setStats(statsRes.data);
      setRisks(risksRes.data || []);
      setReminders(remRes.data || []);
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await projectAPI.delete(id);
      setProjects(prev => prev.filter(p => p.id !== id));
      toast.success('Project deleted');
    } catch { toast.error('Delete failed'); }
  };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="dashboard-page">
      {/* Top Nav */}
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-black/40 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/dashboard')}>
              <Terminal className="w-5 h-5 text-indigo-400" />
              <span className="text-base font-bold text-white" style={{fontFamily: 'Space Grotesk, sans-serif'}}>BuilderShift.ai</span>
            </div>
            <div className="hidden md:flex items-center gap-1 text-sm">
              <Button variant="ghost" size="sm" className="text-white/50 hover:text-white" onClick={() => navigate('/dashboard')} data-testid="nav-dashboard">Dashboard</Button>
              <Button variant="ghost" size="sm" className="text-white/50 hover:text-white" onClick={() => navigate('/projects')} data-testid="nav-projects">Projects</Button>
              <Button variant="ghost" size="sm" className="text-white/50 hover:text-white" onClick={() => navigate('/tokens')} data-testid="nav-tokens">Tokens</Button>
              <Button variant="ghost" size="sm" className="text-white/50 hover:text-white" onClick={() => navigate('/logs')} data-testid="nav-logs">Logs</Button>
              <Button variant="ghost" size="sm" className="text-white/50 hover:text-white" onClick={() => navigate('/vscode')} data-testid="nav-vscode">VS Code</Button>
              {user?.is_admin && (
                <Button variant="ghost" size="sm" className="text-indigo-400 hover:text-indigo-300" onClick={() => navigate('/admin')} data-testid="nav-admin">
                  <Shield className="w-3.5 h-3.5 mr-1" /> Admin
                </Button>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')} data-testid="dashboard-theme-toggle">
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
            <div className="flex items-center gap-2 bg-white/5 rounded-md px-3 py-1.5 border border-white/10">
              <Coins className="w-4 h-4 text-amber-400" />
              <span className="text-sm font-medium text-white" data-testid="token-balance">{user?.token_balance || 0}</span>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="text-white/60 hover:text-white" data-testid="user-menu-trigger">
                  {user?.name || 'User'} <ChevronDown className="w-3.5 h-3.5 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-[#0a0a0a] border-white/10 text-white min-w-[160px]">
                <DropdownMenuItem className="text-white/60 hover:text-white focus:text-white focus:bg-white/5" onClick={() => navigate('/settings')} data-testid="menu-settings">
                  <Settings className="w-4 h-4 mr-2" /> Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem className="text-red-400 hover:text-red-300 focus:text-red-300 focus:bg-white/5" onClick={logout} data-testid="menu-logout">
                  <LogOut className="w-4 h-4 mr-2" /> Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Welcome */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white tracking-tight" style={{fontFamily: 'Space Grotesk, sans-serif'}} data-testid="dashboard-title">
              Welcome back, {user?.name}
            </h1>
            <p className="text-white/40 mt-1">Manage your projects and track usage</p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" className="border-white/20 text-white hover:border-white/40" onClick={() => navigate('/new-project?type=existing')} data-testid="connect-repo-btn">
              <GitBranch className="w-4 h-4 mr-2" /> Existing Project
            </Button>
            <Button className="bg-indigo-600 hover:bg-indigo-500 text-white shadow-[0_0_20px_rgba(99,102,241,0.3)]" onClick={() => navigate('/new-project')} data-testid="new-project-btn">
              <Plus className="w-4 h-4 mr-2" /> New Project
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-white/[0.03] border-white/10" data-testid="stat-projects">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium tracking-wider uppercase text-white/30">Projects</p>
                  <p className="text-2xl font-bold text-white mt-1">{projects.length}</p>
                </div>
                <FolderOpen className="w-8 h-8 text-indigo-400/50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white/[0.03] border-white/10" data-testid="stat-tokens">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium tracking-wider uppercase text-white/30">Token Balance</p>
                  <p className="text-2xl font-bold text-amber-400 mt-1">{user?.token_balance || 0}</p>
                </div>
                <Coins className="w-8 h-8 text-amber-400/50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white/[0.03] border-white/10" data-testid="stat-spent">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium tracking-wider uppercase text-white/30">Tokens Used</p>
                  <p className="text-2xl font-bold text-white mt-1">{stats?.total_tokens_spent || 0}</p>
                </div>
                <Activity className="w-8 h-8 text-cyan-400/50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white/[0.03] border-white/10" data-testid="stat-generations">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium tracking-wider uppercase text-white/30">Generations</p>
                  <p className="text-2xl font-bold text-white mt-1">{stats?.stats?.length || 0}</p>
                </div>
                <Code2 className="w-8 h-8 text-emerald-400/50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Risks + Reminders */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
          <Card className="bg-white/[0.03] border-white/10" data-testid="dashboard-risks">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold text-white flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-400" /> Project Risks
                <Badge variant="outline" className="ml-auto text-white/40 border-white/10 text-[10px]">
                  {risks.reduce((s, r) => s + (r.risk_count || 0), 0)} risks · {risks.length} project{risks.length !== 1 ? 's' : ''}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 max-h-[360px] overflow-auto">
              {risks.length === 0 ? (
                <div className="text-center py-8 text-white/30 text-sm">
                  <ListChecks className="w-8 h-8 opacity-30 mx-auto mb-2" />
                  All your projects look healthy
                </div>
              ) : (
                <div className="space-y-2.5">
                  {risks.slice(0, 6).map((p) => (
                    <div
                      key={p.project_id}
                      className="bg-white/5 rounded-lg p-3 border border-white/5 hover:border-amber-500/30 cursor-pointer transition-colors"
                      onClick={() => navigate(`/project/${p.project_id}`)}
                      data-testid={`risk-project-${p.project_id}`}
                    >
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-sm font-medium text-white truncate">{p.project_name}</span>
                        <Badge className={`text-[9px] ${p.score >= 8 ? 'bg-red-500/20 text-red-300 border-red-500/30' : p.score >= 4 ? 'bg-amber-500/20 text-amber-300 border-amber-500/30' : 'bg-sky-500/20 text-sky-300 border-sky-500/30'}`}>
                          {p.risk_count} risk{p.risk_count !== 1 ? 's' : ''}
                        </Badge>
                      </div>
                      <div className="space-y-1">
                        {p.top_risks.map((r, idx) => (
                          <div key={idx} className="flex items-start gap-2 text-xs">
                            <span className={`mt-1 w-1.5 h-1.5 rounded-full shrink-0 ${
                              r.level === 'critical' ? 'bg-red-400' : r.level === 'high' ? 'bg-orange-400' : r.level === 'medium' ? 'bg-amber-400' : 'bg-sky-400'
                            }`} />
                            <span className="text-white/60 flex-1">{r.title}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-white/[0.03] border-white/10" data-testid="dashboard-reminders">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold text-white flex items-center gap-2">
                <Bell className="w-4 h-4 text-indigo-400" /> Reminders
                <Badge variant="outline" className="ml-auto text-white/40 border-white/10 text-[10px]">
                  {reminders.length}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 max-h-[360px] overflow-auto">
              {reminders.length === 0 ? (
                <div className="text-center py-8 text-white/30 text-sm">
                  <Bell className="w-8 h-8 opacity-30 mx-auto mb-2" />
                  No reminders right now
                </div>
              ) : (
                <div className="space-y-2">
                  {reminders.map((r, idx) => {
                    const IconMap = { alert: AlertTriangle, clock: Clock, 'alert-octagon': AlertOctagon, zap: Zap, calendar: Calendar };
                    const I = IconMap[r.icon] || Bell;
                    return (
                      <div
                        key={idx}
                        className="bg-white/5 rounded-lg p-3 border border-white/5 hover:border-indigo-500/30 cursor-pointer transition-colors flex items-start gap-3"
                        onClick={() => navigate(`/project/${r.project_id}`)}
                        data-testid={`reminder-${r.type}-${r.project_id}`}
                      >
                        <I className={`w-4 h-4 shrink-0 mt-0.5 ${
                          r.type === 'open_showstoppers' ? 'text-red-400' :
                          r.type === 'overdue_tasks' ? 'text-orange-400' :
                          r.type === 'due_soon' ? 'text-amber-400' :
                          r.type === 'sprint_ending' ? 'text-purple-400' :
                          'text-indigo-400'
                        }`} />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-white/80 leading-snug">{r.title}</p>
                          <p className="text-[10px] text-white/40 mt-0.5">{r.project_name}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Projects */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-white" style={{fontFamily: 'Space Grotesk, sans-serif'}}>Your Projects</h2>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1,2,3].map(i => (
              <Card key={i} className="bg-white/[0.03] border-white/10 animate-pulse h-40" />
            ))}
          </div>
        ) : projects.length === 0 ? (
          <Card className="bg-white/[0.03] border-white/10 border-dashed" data-testid="empty-projects">
            <CardContent className="p-12 text-center">
              <FolderOpen className="w-12 h-12 text-white/20 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-white/60 mb-2">No projects yet</h3>
              <p className="text-sm text-white/30 mb-6">Create your first project to get started</p>
              <Button className="bg-indigo-600 hover:bg-indigo-500 text-white" onClick={() => navigate('/new-project')} data-testid="empty-new-project-btn">
                <Plus className="w-4 h-4 mr-2" /> New Project
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {projects.map((project) => {
              const Icon = typeIcons[project.project_type] || Code2;
              return (
                <Card
                  key={project.id}
                  className="bg-white/[0.03] border-white/10 hover:border-white/20 cursor-pointer transition-all hover:-translate-y-0.5"
                  onClick={() => navigate(`/project/${project.id}`)}
                  data-testid={`project-card-${project.id}`}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-10 h-10 rounded-lg bg-indigo-500/10 flex items-center justify-center">
                        <Icon className="w-5 h-5 text-indigo-400" />
                      </div>
                      <Badge variant="outline" className="text-white/40 border-white/10 text-[10px]">
                        {typeLabels[project.project_type] || project.project_type}
                      </Badge>
                    </div>
                    <h3 className="text-base font-semibold text-white mb-1 truncate">{project.name}</h3>
                    <p className="text-xs text-white/30 truncate mb-3">{project.description || 'No description'}</p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1 text-xs text-white/20">
                        <Clock className="w-3 h-3" />
                        {new Date(project.created_at).toLocaleDateString()}
                      </div>
                      <div className="flex items-center gap-1 text-xs text-amber-400/60">
                        <Coins className="w-3 h-3" /> {project.total_tokens_spent}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
